import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import { AnalyticsAnalysisOverlay } from '@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
    type DisplayMessages,
    getDisplayMessages,
    getLocale,
    getUserTenant,
    isUserSessionExpired,
} from '../configuration';
import { AppHeaderContainer } from './AppHeaderContainer';
import { AppSubNavigationContainer } from './AppSubNavigationContainer';
import { fetchPermissionsThunk } from './app/common/permissions/permissions.thunk';
import { initToggles } from './app/common/toggles/featureToggles';
import { useMarketplaceBottomBar } from './app/customerCenter/MarketplaceBottomBar';
import { MarketplaceContainer } from './app/MarketplaceContainer';
import { MarketplaceSidebarTargetContext } from './marketplaceSidebarContext';
import './app/common/IntlDateTimePicker';

import { type RootDispatch, type RootState, useAppDispatch } from '../configuration/setup/store';
import { freeTrialStatusThunk } from './app/freeTrial/freeTrialStatus.thunk';
import { getIsOpenedInPopup } from './app/popup/popup.redux';
import { usePopupDeepLinkingHook } from './app/popup/popupDeepLinkingHook';

const isDev = import.meta.env.DEV;

export function shouldShowAnalyticsAnalysisOverlay() {
    return localStorage.getItem('marketplace.analyticsoverlay') === 'true';
}

// Initialize extra-globals and toggles
initToggles();

interface AppContainerProps {
    fetchPermissions: () => void;
    userLocale: string;
    userTenant?: string;
    showSessionExpired: boolean;
    displayMessages: DisplayMessages;
    isOpenedInPopup: boolean;
}

function App(props: AppContainerProps) {
    const {
        displayMessages,
        showSessionExpired,
        userLocale: locale,
        userTenant,
        isOpenedInPopup,
        fetchPermissions,
    } = props;

    useEffect(() => {
        verifyAndRedirectToLatamMarketplace(userTenant);
    }, [userTenant]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);
    usePopupDeepLinkingHook();

    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(freeTrialStatusThunk);
    }, []);

    const MarketplaceBottomBar = useMarketplaceBottomBar();
    const [forcedClose, setForcedClose] = useState(false);
    const [sidebarTarget, setSidebarTarget] = useState<HTMLDivElement | null>(null);

    if (!displayMessages) {
        return null;
    }

    return (
        <MarketplaceSidebarTargetContext.Provider value={sidebarTarget}>
            <ApplicationLayout>
                {!isOpenedInPopup && <AppHeaderContainer />}
                <ApplicationLayout.Sidebar className='right' ref={setSidebarTarget} />
                <ApplicationLayout.Body
                    bottomBar={MarketplaceBottomBar}
                    banner={!isOpenedInPopup && <AppSubNavigationContainer />}
                >
                    <NotificationsContainer />
                    <SessionExpiredDialog
                        locale={locale}
                        onClose={() => setForcedClose(true)}
                        show={showSessionExpired && !forcedClose}
                    />
                    <MarketplaceContainer />
                </ApplicationLayout.Body>
                {shouldShowAnalyticsAnalysisOverlay() && (
                    <AnalyticsAnalysisOverlay
                        bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                    />
                )}
            </ApplicationLayout>
        </MarketplaceSidebarTargetContext.Provider>
    );
}

const verifyAndRedirectToLatamMarketplace = (tenant?: string) => {
    if (tenant?.startsWith('rio-brazil.')) {
        const currentPath = window.location.pathname;
        const currentQueryString = window.location.search;
        const targetURL = 'https://latam-marketplace.rio.cloud';
        window.location.href = targetURL + currentPath + currentQueryString;
    }
};

export const mapDispatchToProps = (dispatch: RootDispatch) => ({
    fetchPermissions: () => dispatch(fetchPermissionsThunk),
});

export const mapStateToProps = (state: RootState) => ({
    displayMessages: getDisplayMessages(state),
    userTenant: getUserTenant(state),
    showSessionExpired: isUserSessionExpired(state),
    userLocale: getLocale(state),
    isOpenedInPopup: getIsOpenedInPopup(state),
});

export const AppContainer = connect(mapStateToProps, mapDispatchToProps)(App);
